import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { type ApiError, api, qs, session, unwrap } from '../api/client';
import type {
  AdminUserRow,
  AdminWorkspaceDetail,
  AdminWorkspaceRow,
  ApiUsageRow,
  AuditRow,
  IntegrationErrorRow,
  IntegrationRow,
  Items,
  PlatformAutomationRow,
  PlatformOverview,
  PlatformSettings,
} from '../api/types';
import { useSession } from '../auth/session';
import {
  Badge,
  Card,
  DataTable,
  Empty,
  ErrorAlert,
  fmtDate,
  PageHeader,
  Stat,
  useToast,
} from '../components/ui';

const PLANS = ['trial', 'basic', 'legend', 'ultra'];
/** ADM-14: the built-in roles an admin can set on a membership. */
const ROLES = ['owner', 'manager', 'operator', 'developer'];

/** ADM-01..12: the platform admin plane; only platform admins see the menu entry, operators read only. */
export function AdminPage() {
  const { me } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const overview = useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: () => unwrap(api.get<PlatformOverview>('/admin/overview')),
    enabled: me?.platformAdmin === true,
  });
  const workspaces = useQuery({
    queryKey: ['admin', 'workspaces', q],
    queryFn: () =>
      unwrap(api.get<Items<AdminWorkspaceRow>>(`/admin/workspaces${qs({ q: q || undefined })}`)),
    enabled: me?.platformAdmin === true,
  });
  const detail = useQuery({
    queryKey: ['admin', 'workspace', selected],
    queryFn: () => unwrap(api.get<AdminWorkspaceDetail>(`/admin/workspaces/${selected}`)),
    enabled: selected !== null,
  });
  const errors = useQuery({
    queryKey: ['admin', 'errors'],
    queryFn: () => unwrap(api.get<Items<IntegrationErrorRow>>('/admin/integration-errors')),
    enabled: me?.platformAdmin === true,
  });
  const audit = useQuery({
    queryKey: ['admin', 'audit'],
    queryFn: () => unwrap(api.get<Items<AuditRow>>('/admin/audit')),
    enabled: me?.platformAdmin === true,
  });
  const users = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => unwrap(api.get<Items<AdminUserRow>>('/admin/users')),
    enabled: me?.platformAdmin === true,
  });
  const integrations = useQuery({
    queryKey: ['admin', 'integrations'],
    queryFn: () => unwrap(api.get<Items<IntegrationRow>>('/admin/integrations')),
    enabled: me?.platformAdmin === true,
  });
  const settings = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => unwrap(api.get<PlatformSettings>('/admin/settings')),
    enabled: me?.platformAdmin === true,
  });
  // ADM-17: API calls per tenant and key over the last week
  const apiUsage = useQuery({
    queryKey: ['admin', 'api-usage'],
    queryFn: () => unwrap(api.get<Items<ApiUsageRow>>('/admin/api-usage?days=7')),
    enabled: me?.platformAdmin === true,
  });
  // ADM-18: the per-workspace report, fetched with the session token like the Reports page
  const downloadCsv = async () => {
    const res = await fetch('/admin/reports/overview.csv', {
      headers: { authorization: `Bearer ${session.get() ?? ''}` },
    });
    if (!res.ok) {
      const reason = (await res.json().catch(() => null)) as {
        error?: { message?: string };
      } | null;
      return toast(reason?.error?.message ?? `Download failed (${res.status})`, 'error');
    }
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'platform-overview.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };
  // ADM-13: every automation on the platform
  const [autoStatus, setAutoStatus] = useState('');
  const allAutomations = useQuery({
    queryKey: ['admin', 'automations', autoStatus],
    queryFn: () =>
      unwrap(
        api.get<Items<PlatformAutomationRow>>(
          `/admin/automations${qs({ status: autoStatus || undefined })}`,
        ),
      ),
    enabled: me?.platformAdmin === true,
  });
  // ADM-12: operators see everything and change nothing
  const readOnly = me?.platformRole !== 'super';
  // the maintenance notice lives on /portal/me too, so the banner follows a settings change
  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['admin'] }),
      qc.invalidateQueries({ queryKey: ['me'] }),
    ]);
  const act = useMutation({
    mutationFn: (v: { method: 'POST' | 'PATCH'; path: string; body: unknown }) =>
      v.method === 'POST'
        ? unwrap(api.post(`/admin${v.path}`, v.body))
        : unwrap(api.patch(`/admin${v.path}`, v.body)),
    onSuccess: () => {
      toast('Done; recorded in the audit log');
      return refresh();
    },
    onError: (e: ApiError) => toast(e.message, 'error'),
  });
  const withReason = (
    method: 'POST' | 'PATCH',
    path: string,
    extra: Record<string, unknown> = {},
  ) => {
    const reason = window.prompt('Reason (recorded in the audit log)');
    if (reason === null) return;
    act.mutate({ method, path, body: { ...extra, reason } });
  };
  if (!me?.platformAdmin) {
    return (
      <>
        <PageHeader title="Admin" subtitle="Platform administration" />
        <Empty>This area is for platform administrators.</Empty>
      </>
    );
  }
  const o = overview.data;
  const d = detail.data;
  return (
    <>
      <PageHeader
        title="Admin"
        subtitle={
          readOnly
            ? 'Every brand, user, automation and integration error on the platform (read-only operator access)'
            : 'Every brand, user, automation and integration error on the platform'
        }
      />
      <ErrorAlert error={(overview.error ?? workspaces.error ?? detail.error) as ApiError | null} />
      {o ? (
        <div className="grid grid-4" data-testid="admin-overview">
          <Stat label="Workspaces" value={o.workspaces} />
          <Stat label="Users" value={o.users} />
          <Stat label="Connected accounts" value={o.connectedAccounts} />
          <Stat label="Active automations" value={o.activeAutomations} />
          <Stat label="Messages sent (7d)" value={o.messagesSent7d} />
          <Stat label="Messages failed (7d)" value={o.messagesFailed7d} />
          <Stat label="API requests (7d)" value={o.apiRequests7d} />
        </div>
      ) : null}
      <div className="grid grid-2">
        <Card title="Delivery queue by plan">
          <p className="muted small">
            Ultra sends first, then Legend, Basic and trial (DLV-12); the oldest wait shows what
            that order costs each plan right now.
          </p>
          <table className="table" data-testid="admin-queue">
            <thead>
              <tr>
                <th>Plan</th>
                <th>Queued</th>
                <th>Sending</th>
                <th>Oldest wait</th>
              </tr>
            </thead>
            <tbody>
              {(o?.queue ?? []).map((r) => (
                <tr key={r.plan}>
                  <td>{r.plan}</td>
                  <td>{r.queued}</td>
                  <td>{r.sending}</td>
                  <td>{r.oldestWaitSeconds === null ? '—' : `${r.oldestWaitSeconds} s`}</td>
                </tr>
              ))}
              {o && (o.queue ?? []).length === 0 ? (
                <tr>
                  <td className="muted" colSpan={4}>
                    Nothing waiting to be sent.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </Card>
        <Card title="API usage (last 7 days)">
          <div className="row" style={{ justifyContent: 'flex-end', marginBottom: 8 }}>
            <button type="button" className="btn btn-sm" onClick={() => void downloadCsv()}>
              Download CSV
            </button>
          </div>
          <ErrorAlert error={apiUsage.error as ApiError | null} />
          <table className="table" data-testid="admin-api-usage">
            <thead>
              <tr>
                <th>Workspace</th>
                <th>API key</th>
                <th>Calls</th>
              </tr>
            </thead>
            <tbody>
              {(apiUsage.data?.items ?? []).map((r) => (
                <tr key={`${r.workspaceId}-${r.apiKeyId ?? 'none'}`}>
                  <td>{r.workspaceName}</td>
                  <td>{r.keyName ?? <span className="muted">deleted key</span>}</td>
                  <td>{r.calls}</td>
                </tr>
              ))}
              {apiUsage.data?.items.length === 0 ? (
                <tr>
                  <td className="muted" colSpan={3}>
                    No API calls in the last 7 days.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </Card>
      </div>
      <Card title="Workspaces">
        <input
          className="input"
          style={{ maxWidth: 260, marginBottom: 8 }}
          placeholder="Search by name"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <DataTable>
          <table className="table" data-testid="admin-workspaces">
            <thead>
              <tr>
                <th>Workspace</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Subscription</th>
                <th>Members</th>
                <th>Accounts</th>
                <th>Active automations</th>
                <th>Runs / hour</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(workspaces.data?.items ?? []).map((w) => (
                <tr key={w.id}>
                  <td>
                    <button
                      type="button"
                      className="btn btn-link"
                      onClick={() => setSelected(w.id)}
                    >
                      {w.name}
                    </button>
                  </td>
                  <td>
                    <select
                      className="input"
                      value={w.plan}
                      disabled={readOnly}
                      aria-label={`plan for ${w.name}`}
                      onChange={(e) =>
                        withReason('PATCH', `/workspaces/${w.id}/plan`, { plan: e.target.value })
                      }
                    >
                      {PLANS.map((p) => (
                        <option key={p}>{p}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <Badge value={w.status} />
                  </td>
                  <td>{w.subscriptionStatus ?? '—'}</td>
                  <td>{w.members}</td>
                  <td>{w.accounts}</td>
                  <td>{w.activeAutomations}</td>
                  <td>
                    {readOnly ? (
                      (w.runsPerHourOverride ?? 'plan')
                    ) : (
                      <input
                        className="input"
                        style={{ width: 90 }}
                        aria-label={`hourly run budget for ${w.name}`}
                        placeholder="plan"
                        defaultValue={w.runsPerHourOverride ?? ''}
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          const next = v === '' ? null : Number(v);
                          if (next === (w.runsPerHourOverride ?? null)) return;
                          withReason('PATCH', `/workspaces/${w.id}/limits`, { runsPerHour: next });
                        }}
                      />
                    )}
                  </td>
                  <td>{fmtDate(w.createdAt)}</td>
                  <td className="actions">
                    {readOnly ? null : w.status === 'active' ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => withReason('POST', `/workspaces/${w.id}/suspend`)}
                      >
                        Suspend
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => withReason('POST', `/workspaces/${w.id}/unsuspend`)}
                      >
                        Unsuspend
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataTable>
      </Card>
      {d ? (
        <Card title={`${d.workspace.name}: members, automations, usage`}>
          <dl className="kv">
            <dt>Runs (7d)</dt>
            <dd>{d.runs7d}</dd>
            <dt>Messages sent / failed (7d)</dt>
            <dd>
              {d.messagesSent7d} / {d.messagesFailed7d}
            </dd>
            <dt>API requests (7d)</dt>
            <dd>{d.apiRequests7d}</dd>
            <dt>Members</dt>
            <dd>{d.members.map((m) => `${m.email} (${m.role})`).join(', ') || '—'}</dd>
          </dl>
          <DataTable>
            <table className="table">
              <thead>
                <tr>
                  <th>Automation</th>
                  <th>Trigger</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {d.automations.map((a) => (
                  <tr key={a.id}>
                    <td>{a.name}</td>
                    <td>{a.triggerType.replace('_', ' ')}</td>
                    <td>
                      <Badge value={a.status} />
                    </td>
                    <td>
                      {readOnly ? null : a.status === 'active' ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => withReason('POST', `/automations/${a.id}/pause`)}
                        >
                          Pause
                        </button>
                      ) : a.status === 'paused' ? (
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => withReason('POST', `/automations/${a.id}/resume`)}
                        >
                          Resume
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DataTable>
        </Card>
      ) : null}
      <Card title="All automations">
        <select
          className="input"
          style={{ maxWidth: 200, marginBottom: 8 }}
          aria-label="automation status filter"
          value={autoStatus}
          onChange={(e) => setAutoStatus(e.target.value)}
        >
          <option value="">All statuses</option>
          {['draft', 'active', 'paused'].map((st) => (
            <option key={st} value={st}>
              {st}
            </option>
          ))}
        </select>
        <DataTable>
          <table className="table" data-testid="admin-automations">
            <thead>
              <tr>
                <th>Automation</th>
                <th>Workspace</th>
                <th>Account</th>
                <th>Trigger</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {(allAutomations.data?.items ?? []).map((a) => (
                <tr key={a.id}>
                  <td>{a.name}</td>
                  <td>{a.workspaceName}</td>
                  <td>{a.accountUsername ? `@${a.accountUsername}` : '—'}</td>
                  <td>{a.triggerType.replace('_', ' ')}</td>
                  <td>
                    <Badge value={a.status} />
                  </td>
                  <td>{fmtDate(a.createdAt)}</td>
                </tr>
              ))}
              {allAutomations.data?.items.length === 0 ? (
                <tr>
                  <td className="muted">No automations on the platform yet.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </DataTable>
      </Card>
      <Card title="Users">
        <DataTable>
          <table className="table" data-testid="admin-users">
            <thead>
              <tr>
                <th>Email</th>
                <th>Verified</th>
                <th>Workspaces</th>
                <th>Status</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(users.data?.items ?? []).map((u) => (
                <tr key={u.id}>
                  <td>{u.email}</td>
                  <td>{u.emailVerified ? 'yes' : 'no'}</td>
                  <td>
                    {readOnly
                      ? u.workspaces.map((w) => `${w.name} (${w.role})`).join(', ') || '—'
                      : u.workspaces.map((w) => (
                          // ADM-14: the membership's built-in role, one select per workspace
                          <label key={w.id} className="row small" style={{ gap: 6 }}>
                            {w.name}
                            <select
                              className="input"
                              style={{ width: 120 }}
                              aria-label={`role for ${u.email} in ${w.name}`}
                              value={w.role}
                              onChange={(e) =>
                                withReason('PATCH', `/users/${u.id}/memberships/${w.id}`, {
                                  role: e.target.value,
                                })
                              }
                            >
                              {(ROLES.includes(w.role) ? ROLES : [...ROLES, w.role]).map((r) => (
                                <option key={r} value={r}>
                                  {r}
                                </option>
                              ))}
                            </select>
                          </label>
                        ))}
                    {!readOnly && u.workspaces.length === 0 ? '—' : null}
                  </td>
                  <td>
                    <Badge value={u.disabledAt ? 'disabled' : 'active'} />
                  </td>
                  <td>{fmtDate(u.createdAt)}</td>
                  <td className="actions">
                    {readOnly ? null : (
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => withReason('POST', `/users/${u.id}/password-reset`)}
                      >
                        Send password reset
                      </button>
                    )}{' '}
                    {readOnly || u.id === me.userId ? null : u.disabledAt ? (
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => withReason('POST', `/users/${u.id}/enable`)}
                      >
                        Enable
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => withReason('POST', `/users/${u.id}/disable`)}
                      >
                        Disable
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataTable>
      </Card>
      <div className="grid grid-2">
        <Card title="Integrations">
          <table className="table" data-testid="admin-integrations">
            <thead>
              <tr>
                <th>Account</th>
                <th>Workspace</th>
                <th>Status</th>
                <th>Health</th>
                <th>Last event</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(integrations.data?.items ?? []).map((a) => (
                <tr key={a.id}>
                  <td>
                    <Badge value={a.platform} /> @{a.username}
                  </td>
                  <td>{a.workspaceName}</td>
                  <td>
                    <Badge value={a.status} />
                  </td>
                  <td>{a.health}</td>
                  <td>{a.lastEventAt ? fmtDate(a.lastEventAt) : '—'}</td>
                  <td className="actions">
                    {readOnly || a.status === 'disconnected' ? null : (
                      <>
                        {/* ADM-15: the hourly sweep's check, now, with a token refresh */}
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => withReason('POST', `/social-accounts/${a.id}/health`)}
                        >
                          Check
                        </button>{' '}
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => withReason('POST', `/social-accounts/${a.id}/disconnect`)}
                        >
                          Disconnect
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {integrations.data?.items.length === 0 ? (
                <tr>
                  <td className="muted">No connected accounts yet.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </Card>
        <Card title="Platform settings">
          {settings.data ? (
            <dl className="kv" data-testid="admin-settings">
              <dt>Sign-ups</dt>
              <dd>
                <Badge value={settings.data.signupsEnabled ? 'open' : 'closed'} />{' '}
                {readOnly ? null : (
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() =>
                      withReason('PATCH', '/settings', {
                        signupsEnabled: !settings.data?.signupsEnabled,
                      })
                    }
                  >
                    {settings.data.signupsEnabled ? 'Close sign-ups' : 'Open sign-ups'}
                  </button>
                )}
              </dd>
              <dt>Maintenance notice</dt>
              <dd>
                {settings.data.maintenanceNotice ?? <span className="muted">none</span>}{' '}
                {readOnly ? null : (
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => {
                      const notice = window.prompt(
                        'Notice shown to every workspace (blank clears it)',
                        settings.data?.maintenanceNotice ?? '',
                      );
                      if (notice === null) return;
                      withReason('PATCH', '/settings', { maintenanceNotice: notice });
                    }}
                  >
                    Edit notice
                  </button>
                )}
              </dd>
            </dl>
          ) : null}
        </Card>
      </div>
      <div className="grid grid-2">
        <Card title="Integration errors">
          <DataTable>
            <table className="table">
              <tbody>
                {(errors.data?.items ?? []).map((e) => (
                  <tr key={`${e.kind}-${e.id}`}>
                    <td>
                      <Badge value={e.kind} />
                    </td>
                    <td className="mono">{e.workspaceId.slice(-8)}</td>
                    <td>{e.error ?? '—'}</td>
                    <td>{fmtDate(e.at)}</td>
                    <td className="actions">
                      {readOnly ? null : (
                        // ADM-16
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() =>
                            withReason('POST', '/integration-errors/retry', {
                              kind: e.kind,
                              id: e.id,
                            })
                          }
                        >
                          Retry
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {errors.data?.items.length === 0 ? (
                  <tr>
                    <td className="muted">No failed messages, deliveries or posts.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </DataTable>
        </Card>
        <Card title="Audit log">
          <table className="table" data-testid="admin-audit">
            <tbody>
              {(audit.data?.items ?? []).map((a) => (
                <tr key={a.id}>
                  <td>{fmtDate(a.createdAt)}</td>
                  <td>{a.actorEmail}</td>
                  <td className="mono">{a.action}</td>
                  <td className="mono">{a.targetId.slice(-8)}</td>
                  <td className="muted">{a.reason ?? ''}</td>
                </tr>
              ))}
              {audit.data?.items.length === 0 ? (
                <tr>
                  <td className="muted">No admin actions yet.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </Card>
      </div>
    </>
  );
}
